'use client'

import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react'
import { Card, CardProps } from '@/ui/components/Card'
import { CardType } from '@/ui/components/Card/types'
import { SortingOption } from '@/ui/components/SortBy'
import classNames from 'classnames'
import { FetchDataParams } from '@/lib/actions/fetchCollectionData'
import { FilterOption } from '@/ui/components/FilterRow'
import { CollectionSlug, PaginatedDocs, TypedLocale } from 'payload'
import { ArchiveHeader } from '@/ui/components/CollectionArchive/ArchiveHeader'
import { ArchiveFiltersAndSort } from '@/ui/components/CollectionArchive/ArchiveFiltersAndSort'
import { ArchivePagination } from '@/ui/components/CollectionArchive/ArchivePagination'
import { ArchiveContent } from '@/ui/components/CollectionArchive/ArchiveContent'
import { ItemsGridSkeleton } from './ArchiveContent/ItemsGrid/ItemsGridSkeleton'
import { useArchiveUrl } from '@/ui/components/CollectionArchive/useArchiveUrl'
import { getUserSeed } from '@/lib/getUserSeed'
import { useUser } from '@/context/optionalClerk'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/tanstack/keys'
import { LockedPreviewGrid } from '@/ui/components/LockedPreviewGrid'
import type { GateConfig } from '@/ui/blocks/shared/gatedArchiveTypes'
import { EmptyState, EmptyStateType } from '@/ui/components/EmptyStates'
import { ButtonField } from '@/payload-types'

/**
 * Props for the CollectionArchive component
 */
export type CollectionArchiveProps = {
  /** Unique identifier for this archive component */
  archiveId: string
  /** Optional filter options */
  filter?: FilterOption[]
  /** Field name to filter on (e.g. 'type', 'category', etc.) */
  filterField?: string
  /** Number of columns in the grid */
  gridCols?: 1 | 2 | 3 | 4
  /** Additional CSS classes */
  className?: string
  /** Title for the filter section */
  filterTitle?: string
  /** Text for the load more button */
  loadMoreText?: string
  /** Show load more button instead of pagination */
  showLoadMoreButton?: boolean
  /** Appearance of the pagination */
  loadMoreStrategy?: 'none' | 'pagination' | 'loadMoreButton'
  /** Sorting options for the collection */
  sortingOptions?: SortingOption[]
  /** Default sort value (used when sortingOptions is not provided but parent manages sorting UI) */
  defaultSort?: string
  /** Limit of items per page */
  limit?: number
  /** Type of card to display */
  cardType?: CardType
  /** Title of the archive */
  title?: string
  /** Intro text displayed below the title */
  introText?: string
  /** Hierarchy level of the title */
  titleHierarchy?: 'h2' | 'h1' | 'h3' | 'h4' | 'h5' | 'h6' | null
  /** Fixed title style regardless of hierarchy */
  fixedTitleStyle?: 'h2' | 'h1' | 'h3' | 'h4' | 'h5' | 'h6' | null
  /** Initial data for the archive */
  initialData: PaginatedDocs<CardProps>
  /** Function to fetch collection data */
  fetchCollectionDataAction: (params: FetchDataParams) => Promise<PaginatedDocs<CardProps>>
  /** Collection to fetch data from */
  collection: CollectionSlug
  /** Function to map docs to card props */
  mapper?: (docs: any[]) => CardProps[] | Promise<CardProps[]>
  /** Locale for fetching data */
  locale: TypedLocale
  /** Where clause for filtering data */
  where: Record<string, any>
  /** Whether to include an "All" filter option */
  includeAllFilter?: boolean
  /** Visual appearance of the filter */
  filterAppearance?: 'default' | 'compact' | 'grid'
  /** Enable analytics tracking for cards */
  enableTracking?: boolean
  /** Page ID for visibleOn filtering */
  pageId?: string
  /** Populate by mode for visibleOn filtering */
  populateBy?: 'auto' | 'all'
  /** Whether content is gated */
  isGated?: boolean
  /** Gate configuration for locked state */
  gateConfig?: GateConfig
  /** Empty state type (e.g., 'articles', 'products', etc.) */
  emptyStateType?: EmptyStateType
  /** Optional button for empty state */
  emptyStateButton?: ButtonField
  /** Whether empty state has a button */
  emptyStateHasButton?: boolean | null
  /** Custom filter builder function for complex filtering logic */
  customFilterBuilder?: (baseWhere: any, filterValue: string, ...args: any[]) => any
  /** Additional context data for custom filter builder */
  filterBuilderContext?: any
}

/**
 * CollectionArchive component displays a collection of items in a grid layout
 * with optional filtering and pagination.
 */
export const CollectionArchive: React.FC<CollectionArchiveProps> = ({
  archiveId = 'default-archive',
  gridCols = 3,
  filter = [],
  filterField,
  className,
  filterTitle = 'Filter articles',
  loadMoreText = 'Load more',
  loadMoreStrategy = 'pagination',
  limit = 10,
  cardType = CardType.Standard,
  title,
  introText,
  sortingOptions,
  defaultSort: defaultSortProp,
  titleHierarchy = 'h2',
  fixedTitleStyle,
  initialData,
  fetchCollectionDataAction,
  collection,
  mapper,
  locale,
  where,
  includeAllFilter = true,
  filterAppearance,
  enableTracking = false,
  pageId,
  populateBy,
  isGated = false,
  gateConfig,
  emptyStateType,
  emptyStateButton,
  emptyStateHasButton,
  customFilterBuilder,
  filterBuilderContext,
}) => {
  // State for user seed and tracking custom params
  const [hadCustomParams, setHadCustomParams] = useState<boolean>(false)
  const [userSeed, setUserSeed] = useState<string | undefined>(undefined)

  const user = useUser()
  const queryClient = useQueryClient()
  const { isSignedIn, isLoaded } = user

  // Use useTransition to keep UI responsive during data fetching
  const [isPending, startTransition] = useTransition()

  // Compute effective title hierarchy: fixedTitleStyle overrides titleHierarchy
  const effectiveTitleHierarchy = fixedTitleStyle ?? titleHierarchy

  // Calculate default sort from sorting options (prefer desc/newest first, otherwise use first option)
  // Or use the provided defaultSort prop if sortingOptions is not provided
  const defaultSort = useMemo(() => {
    // If defaultSort prop is provided, use it
    if (defaultSortProp !== undefined) return defaultSortProp

    // Otherwise calculate from sortingOptions
    if (!sortingOptions || sortingOptions.length === 0) return undefined
    // Prefer desc (newest first) if available, otherwise use first option
    const descOption = sortingOptions.find((opt) => opt.dir === 'desc')
    const defaultOption = descOption || sortingOptions[0]
    if (!defaultOption) return undefined
    return `${defaultOption.dir === 'desc' ? '-' : ''}${defaultOption.propertyName}`
  }, [sortingOptions, defaultSortProp])

  // Archive URL params - the single source of truth
  const {
    currentPage,
    currentFilter,
    currentSort,
    paginationId,
    filterId,
    sortingId,
    updateUrlParams,
    hasCustomParams,
    archiveSpecificParams,
  } = useArchiveUrl(archiveId, undefined)

  // Compute filter where clause (needs to be in query key)
  const filterWhere = useMemo(() => {
    if (!filterField) return where

    // Use custom filter builder if provided
    if (customFilterBuilder) {
      return customFilterBuilder(where, currentFilter, filterBuilderContext)
    }

    // Default filter logic
    return currentFilter !== 'all'
      ? {
          ...where,
          [filterField]: {
            equals: currentFilter,
          },
        }
      : where
  }, [filterField, currentFilter, where, customFilterBuilder, filterBuilderContext])

  // Build query key
  const queryKey = useMemo(
    () =>
      queryKeys.collectionArchive.list(
        archiveId,
        collection,
        locale,
        filterWhere,
        currentPage,
        limit,
        currentSort || defaultSort,
        isSignedIn ?? false,
      ),
    [
      archiveId,
      collection,
      locale,
      filterWhere,
      currentPage,
      limit,
      currentSort,
      defaultSort,
      isSignedIn,
    ],
  )

  // Smart enabled logic: preserve initial data optimization
  // Only use server initialData if: no custom params AND user is NOT signed in
  // (server always fetches with signedIn: false, so if user is signed in, we need fresh data)
  const shouldUseInitialData = !hasCustomParams() && !hadCustomParams && !isSignedIn

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      // Prepare sort parameter
      const sort = currentSort || defaultSort

      const actionParams = {
        signedIn: isSignedIn ?? false,
        collection,
        where: filterWhere,
        locale,
        page: currentPage,
        limit,
        sort,
        mapper,
        pageId,
        populateBy,
      }

      return await fetchCollectionDataAction(actionParams)
    },
    enabled: !shouldUseInitialData,
    initialData: shouldUseInitialData ? initialData : undefined,
    refetchOnMount: !shouldUseInitialData,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  // Derive state from query
  const items = query.data ?? initialData
  const isLoading = query.isLoading
  const isFetching = query.isFetching
  const error = query.error as Error | null

  // Watch auth state changes and refetch when user signs in/out
  const prevSignedInRef = useRef<boolean | undefined>(isSignedIn)

  useEffect(() => {
    // Skip on initial mount
    if (prevSignedInRef.current === undefined) {
      prevSignedInRef.current = isSignedIn
      return
    }

    // Check if auth state actually changed
    if (prevSignedInRef.current !== isSignedIn) {
      prevSignedInRef.current = isSignedIn

      // Invalidate and refetch this archive's queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.collectionArchive.byArchive(archiveId),
      })
    }
  }, [isSignedIn, archiveId, queryClient])

  // Show filter only if there are filter options
  const showFilter = filter && filter.length > 1

  // Fetch user seed on mount
  useEffect(() => {
    const fetchUserSeed = async () => {
      try {
        const seed = await getUserSeed()
        setUserSeed(seed)
      } catch (err) {
        console.error('Error fetching user seed:', err)
        setUserSeed('anonymous')
      }
    }
    fetchUserSeed()
  }, [])

  // Handle filter changes - ONLY updates URL
  const handleFilterChange = useCallback(
    (filterValue: string) => {
      // Start a transition to keep UI responsive
      startTransition(() => {
        // Update URL params - reset pagination when filter changes
        updateUrlParams({
          [filterId]: filterValue === 'all' ? null : filterValue,
          [paginationId]: null, // Reset page when filter changes
        })
      })
    },
    [filterId, paginationId, updateUrlParams],
  )

  // Handle pagination - ONLY updates URL
  const handlePageChange = useCallback(
    (page: number) => {
      // Start a transition to keep UI responsive
      startTransition(() => {
        // Update URL params for pagination
        updateUrlParams({
          [paginationId]: page.toString(),
        })
      })
    },
    [paginationId, updateUrlParams],
  )

  // Handle sorting - ONLY updates URL
  const handleSortChange = useCallback(
    (sortValue: string) => {
      // Start a transition to keep UI responsive
      startTransition(() => {
        // Update URL params - reset pagination when sort changes
        updateUrlParams({
          [sortingId]: sortValue || null,
          [paginationId]: null, // Reset page when sort changes
        })
      })
    },
    [paginationId, sortingId, updateUrlParams],
  )

  // Update hadCustomParams when URL params change
  useEffect(() => {
    if (hasCustomParams()) {
      setHadCustomParams(true)
    }
  }, [hasCustomParams])

  // Show loading skeleton during Clerk initialization
  if (!isLoaded) {
    return (
      <div className={classNames('relative', className)}>
        <Suspense>
          <ArchiveHeader
            title={title}
            introText={introText}
            titleHierarchy={effectiveTitleHierarchy}
          />
        </Suspense>
        <ItemsGridSkeleton cardType={cardType} gridCols={gridCols} limit={limit} showImage={true} />
      </div>
    )
  }

  // Priority 1: Empty state (highest priority)
  // Show empty state regardless of gating
  if (items.totalDocs === 0) {
    return (
      <div className={classNames('relative', className)}>
        <Suspense>
          <ArchiveHeader
            title={title}
            introText={introText}
            titleHierarchy={effectiveTitleHierarchy}
          />

          {/* Show filters even when empty so users can change selection */}
          <ArchiveFiltersAndSort
            filterTitle={filterTitle}
            filter={filter}
            currentFilter={currentFilter}
            onFilterChange={handleFilterChange}
            isLoading={isFetching}
            isPending={isPending}
            sortingOptions={sortingOptions}
            currentSort={currentSort || defaultSort}
            onSortChange={handleSortChange}
            showFilter={showFilter}
            includeAllFilter={includeAllFilter}
            filterAppearance={filterAppearance}
            analyticsContext={{ filter_category: collection }}
          />
        </Suspense>
        {emptyStateType ? (
          <EmptyState
            type={emptyStateType}
            button={emptyStateButton}
            hasButton={emptyStateHasButton ?? false}
            className="mt-8"
          />
        ) : (
          <div className="text-center py-12">
            <p className="text-body-M text-text-subtle">No items found.</p>
          </div>
        )}
      </div>
    )
  }

  // Priority 2: Gated preview (if not signed in)
  // Show LockedPreviewGrid with preview cards
  if (isGated && !isSignedIn && gateConfig) {
    return (
      <div className={classNames('relative', className)}>
        <Suspense>
          <ArchiveHeader
            title={title}
            introText={introText}
            titleHierarchy={effectiveTitleHierarchy}
          />
        </Suspense>
        <LockedPreviewGrid
          previewCards={items.docs}
          currentPath={gateConfig.currentPath}
          gridCols={gridCols}
          title={gateConfig.title}
          message={gateConfig.message}
          signUpButtonLabel={gateConfig.signUpButtonLabel}
          signInButtonLabel={gateConfig.signInButtonLabel}
        />
      </div>
    )
  }

  // Priority 3: Normal full archive with filters/pagination/sorting
  return (
    <div className={classNames('relative', className)}>
      {/* Archive header with title and intro */}
      <Suspense>
        <ArchiveHeader
          title={title}
          introText={introText}
          titleHierarchy={effectiveTitleHierarchy}
        />

        {/* Filters and sorting */}
        <ArchiveFiltersAndSort
          filterTitle={filterTitle}
          filter={filter}
          currentFilter={currentFilter}
          onFilterChange={handleFilterChange}
          isLoading={isFetching}
          isPending={isPending}
          sortingOptions={sortingOptions}
          currentSort={currentSort || defaultSort}
          onSortChange={handleSortChange}
          showFilter={showFilter}
          includeAllFilter={includeAllFilter}
          filterAppearance={filterAppearance}
          analyticsContext={{ filter_category: collection }}
        />

        {/* Error message */}
        {error && (
          <div className="text-red-600 mb-4 p-4 bg-red-50 rounded">
            {process.env.NODE_ENV === 'production'
              ? 'Failed to load content. Please try again.'
              : error.message}
          </div>
        )}

        {/* Loading state */}
        {isLoading ? (
          <ItemsGridSkeleton
            cardType={cardType}
            gridCols={gridCols}
            limit={limit}
            showImage={true}
          />
        ) : (
          <>
            {/* Archive content */}
            <ArchiveContent
              items={items}
              gridCols={gridCols}
              isLoading={isLoading}
              cardType={cardType}
              limit={limit}
              enableTracking={enableTracking}
              userId={userSeed}
            />

            {/* Pagination */}
            <ArchivePagination
              loadMoreStrategy={loadMoreStrategy}
              currentPage={currentPage}
              totalPages={items.totalPages}
              onPageChange={handlePageChange}
              isLoading={isFetching}
              paginationId={paginationId}
              loadMoreText={loadMoreText}
            />
          </>
        )}
      </Suspense>
    </div>
  )
}
